import { booleanAttribute, ChangeDetectionStrategy, Component, computed, inject, input, model } from '@angular/core';
import { DsTooltipDirectiveModule, TooltipConfig } from '@bmw-ds/components';

import { CommunicationField } from '../../../customer/model/communication-field.model';
import { CustomerContact } from '../../../customer/model/customer-information.model';
import { GCDM_MAX_LENGTH } from '../../../customer/model/gcdm-constraints';
import { FeatureVisibilityService } from '../../../customer/services/feature-visibility.service';
import { InputFieldComponent } from '../../../form/components/input-field/input-field.component';
import { InputPhoneNumberComponent } from '../../../form/components/input-phone-number/input-phone-number.component';
import { useI18n } from '../../../i18n/services/i18n.service';
import { lensProxy } from '../../../utils/lensProxy/lensProxy';

@Component({
  selector: 'cdf-profile-edit-communication',
  imports: [InputFieldComponent, DsTooltipDirectiveModule, InputPhoneNumberComponent],
  host: { class: 'd-flex flex-column gap-2x' },
  template: `
    <h3>{{ isPrimaryContactData() ? i18n('primaryContact.contactData') : i18n('businessContact.contactData') }}</h3>
    <cdf-input-field
      [id]="CommunicationField.EMAIL"
      [placeholder]="i18n('placeholder.email')"
      [(model)]="contactProxy.email"
      [displayError]="invalidCommunicationFields().includes(CommunicationField.EMAIL)"
      [errorMessage]="i18n('customerInformationDialog.error.invalidEmail')"
      [disabled]="disabled() || editingEmailDisabled()"
      [maxLength]="gcdmMaxLength.email"
      [ds-tooltip]="i18n('customerInformationDialog.emailDisabledTooltip')"
      [dsTooltipConfig]="disabledEmailTooltipConfig()"
      [hidden]="featureVisibilityService.isHidden(CommunicationField.EMAIL)"
    />
    <cdf-input-phone-number
      [id]="CommunicationField.PHONE"
      [placeholder]="i18n('placeholder.phone')"
      [(model)]="contactProxy.phone"
      [displayError]="invalidCommunicationFields().includes(CommunicationField.PHONE)"
      [errorMessage]="i18n('customerInformationDialog.error.invalidPhone')"
      [disabled]="disabled()"
      [maxLength]="gcdmMaxLength.phone"
      [hidden]="featureVisibilityService.isHidden(CommunicationField.PHONE)"
    />
    <cdf-input-phone-number
      [id]="CommunicationField.MOBILE_PHONE"
      [placeholder]="i18n('placeholder.mobilePhone')"
      [(model)]="contactProxy.mobile"
      [displayError]="invalidCommunicationFields().includes(CommunicationField.MOBILE_PHONE)"
      [errorMessage]="i18n('customerInformationDialog.error.invalidPhone')"
      [disabled]="disabled()"
      [maxLength]="gcdmMaxLength.phone"
      [hidden]="featureVisibilityService.isHidden(CommunicationField.MOBILE_PHONE)"
    />
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProfileEditCommunicationComponent {
  readonly i18n = useI18n();
  readonly featureVisibilityService = inject(FeatureVisibilityService);

  readonly contact = model<CustomerContact>();
  readonly contactProxy = lensProxy(this.contact);
  readonly isPrimaryContactData = input(false, { transform: booleanAttribute });
  readonly invalidCommunicationFields = input<CommunicationField[]>([]);

  readonly disabled = input(false);
  readonly editingEmailDisabled = input(false);
  protected readonly gcdmMaxLength = GCDM_MAX_LENGTH;

  protected readonly disabledEmailTooltipConfig = computed(
    () => ({ origin: 'bottom', disabled: !this.editingEmailDisabled(), showTriggerDelay: 500 }) satisfies TooltipConfig,
  );

  readonly CommunicationField = CommunicationField;
}
